import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IoMenuSharp, IoCloseSharp } from 'react-icons/io5';
import { Link, NavLink } from 'react-router-dom';
import { menuItems as defaultMenuItems } from '@mockData';
import { HandleAuthPopup } from '../commonHelper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store';
import { ContactForm } from '@/pages/contactUsScreen';
import { AppString, toastSuccessHelper } from '@utils';
import { ContactUs } from '@services';
import { setIsUserLoggedInHandler } from '@/store/slice';
import { appLogo } from '@images';

interface FormData {
	email: string;
	message: string;
}

const Menu = ({
	items,
	toggleMenu,
	authOpen,
	contactOpen,
}: {
	items: typeof defaultMenuItems;
	toggleMenu: () => void;
	authOpen: () => void;
	contactOpen: () => void;
}) => (
	<ul className="space-y-2 text-center   font-semibold">
		{items.map((item, index) => (
			<li key={index} className="w-full">
				{['Login / Sign up', 'Contact us / Report an issue'].includes(
					item.label
				) ? (
					<div
						onClick={item.label === 'Login / Sign up' ? authOpen : contactOpen}
						className={`block w-full py-2 px-4 border-t border-gray-200 hover:bg-gray-100 hover:text-blue-700 cursor-pointer`}
					>
						{item.label}
					</div>
				) : (
					<NavLink
						to={item.to}
						onClick={() => {
							toggleMenu();
						}}
						className={({ isActive }) =>
							`block w-full py-2 px-4 ${isActive && item.to != '#' ? 'bg-[#324B69] text-gray-200' : 'border-t border-gray-200'} hover:bg-gray-100 hover:text-blue-700`
						}
					>
						{item.label}
					</NavLink>
				)}
			</li>
		))}
	</ul>
);

export const Header: React.FC = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isAuthModelOpen, setIsAuthModelOpen] = useState(false);
	const [isContentOpen, setIsContentOpen] = useState(false);
	const [fullName, setFullName] = useState('');
	const {
		addressState: {
			properties: {
				addendum: {
					pad: { bbl },
				},
			},
		},
		districtOptionState,
		customSearchOptionState: { navigateParam },
	} = useSelector((state: RootState) => state.rootSlice.application);

	const modalRef = useRef<HTMLDivElement | null>(null);
	const contactModalRef = useRef<HTMLDivElement | null>(null);
	const { isLoggedIn } = useSelector(
		(state: RootState) => state.rootSlice.application
	);
	const dispatch = useDispatch();

	const openModal = useCallback(() => setIsAuthModelOpen(true), []);
	const closeModal = useCallback(() => setIsAuthModelOpen(false), []);
	const openContactModel = useCallback(() => setIsContentOpen(true), []);
	const closeContactModel = useCallback(() => setIsContentOpen(false), []);

	const handleContactSubmit = async (data: FormData) => {
		try {
			const response = await ContactUs(data);
			if (response.status === 200)
				toastSuccessHelper('Message sent successfully!');
		} catch (e) {
			console.log(e);
		} finally {
			closeContactModel();
		}
	};

	const handleLogout = () => {

		localStorage.removeItem(AppString.email_id);
		localStorage.removeItem(AppString.user_token);
		localStorage.removeItem(AppString.full_name);
		setFullName('');
		dispatch(setIsUserLoggedInHandler(false));
		toastSuccessHelper('Logout successful!');
	};

	// Close the modal when clicking outside the modal container
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				closeModal();
			}
		};

		if (isAuthModelOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isAuthModelOpen]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				contactModalRef.current &&
				!contactModalRef.current.contains(event.target as Node)
			) {
				closeContactModel();
			}
		};

		if (isContentOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isContentOpen]);

	const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

	const menuItems = defaultMenuItems.map((item) => {
		if (item.label === 'Property Lookup') {
			return { ...item, to: bbl !== '' ? '/property' : item.to };
		}
		if (item.label === 'District Dashboard') {
			return {
				...item,
				to:
					districtOptionState.id !== ''
						? `/district-dashboard/${districtOptionState.module}/${districtOptionState.id}`
						: item.to,
			};
		}
		if (item.label === 'Custom Search') {
			return {
				...item,
				to: navigateParam !== '' ? `/search${navigateParam}` : item.to,
			};
		}
		return item;
	});

	// Filter the menuItems to exclude 'Login / Sign up' when user is logged in
	const filteredMenuItems = menuItems.filter(
		(item) =>
			!(item.label === 'Login / Sign up' && isLoggedIn) &&
			!(item.label === 'My Dashboard' && !isLoggedIn)
	);

	const customClass = {
		NavLinkClass:
			'py-2 px-6 hover:text-blue-700 hover:bg-[#324B69] hover:text-white border border-[#324B69] rounded-lg font-semibold text-[14px]',
	};

	return (
		<div className=" bg-[#f6f8f8] ">
			<div className="flex items-center justify-between lg:justify-between mx-2 ">
				<div>
					<button
						type="button"
						onClick={toggleMenu}
						className="inline-flex items-center p-2 w-10 h-10 justify-center text-lg rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
					>
						<span className="sr-only">Open main menu</span>
						<IoMenuSharp className="w-10 h-10" />
					</button>
					<h1 className="md:text-[35px] font-bold capitalize leading-10">
						NYAA PROPERTY PORTAL
					</h1>
				</div>

				{/* Logo Section */}
				<div className="flex items-center justify-between">
					<img
						src={appLogo}
						className="h-full w-[120px] md:w-[200px]"
						alt="Logo"
					/>
				</div>
			</div>
			<nav className="border-b-2 border-gray-200">
				<div className="flex items-center justify-between flex-wrap lg:pb-2">
					<div className="hidden lg:flex space-x-2 ml-5">
						{filteredMenuItems
							.slice(0, isLoggedIn ? 5 : 4)
							.map((item, index) => (
								<NavLink
									key={index}
									to={item.to}
									className={({ isActive }) =>
										isActive
											? `${customClass.NavLinkClass} bg-[#324B69] text-white`
											: `${customClass.NavLinkClass}`
									}
								>
									{item.label}
								</NavLink>
							))}
					</div>
					<div className="hidden lg:flex space-x-6 mr-5 ml-auto">
						{isLoggedIn ? (
							<div className="flex items-center justify-end space-x-4">
								<Link
									to="#"
									className="py-2 px-3 text-[14px] hover:text-blue-700 hover:underline"
									onClick={openContactModel}
								>
									Contact us / Report an issue
								</Link>

								<span className="py-2 px-3 text-[14px] font-medium">
									Welcome, {fullName}
								</span>
								<button
									onClick={handleLogout}
									className="btn-light p-1 rounded-sm"
								>
									Logout
								</button>
							</div>
						) : (
							filteredMenuItems.slice(isLoggedIn ? 5 : 4).map((item, index) => (
								<Link
									key={index}
									to={item.to}
									className="py-2 px-3 text-[14px] hover:text-blue-700 hover:underline"
									onClick={
										item.label === 'Login / Sign up'
											? openModal
											: openContactModel
									}
								>
									{item.label}
								</Link>
							))
						)}
					</div>
				</div>

				{/* Overlay Menu for mobile view */}
				{isMenuOpen && (
					<div className="absolute top-0 left-0 bg-white z-[100] flex flex-col text-center p-4 space-y-4 shadow-lg w-[350px] lg:hidden">
						<button
							onClick={toggleMenu}
							className="flex items-center space-x-2 p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 font-semibold rounded-lg"
						>
							<IoCloseSharp className="w-6 h-6" />
							<span className="text-sm uppercase">Close</span>
						</button>

						{/* Logo in Overlay */}
						<div className="flex items-center justify-center w-full">
							{/* <img
								src="https://flowbite.com/docs/images/logo.svg"
								className="h-8"
								alt="Logo"
							/> */}
							<span className="ml-2 text-xl font-semibold">
								NYAA Property Portal
							</span>
						</div>

						{/* Menu Items in Overlay - show user info if logged in */}
						{isLoggedIn && (
							<div className="p-3 bg-gray-100 rounded-lg mb-4">
								<p className="font-medium">Welcome, {fullName}</p>
								<button
									onClick={handleLogout}
									className="mt-2 py-2 px-4 w-full bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
								>
									Logout
								</button>
							</div>
						)}

						{/* Display filtered menu items */}
						<Menu
							items={filteredMenuItems}
							toggleMenu={toggleMenu}
							authOpen={openModal}
							contactOpen={openContactModel}
						/>
					</div>
				)}
			</nav>
			<div>
				{isAuthModelOpen && (
					<HandleAuthPopup closeModal={closeModal} modelref={modalRef} />
				)}
				{isContentOpen && (
					<ContactForm
						onClose={closeContactModel}
						modelref={contactModalRef}
						onSubmit={handleContactSubmit}
					/>
				)}
			</div>
		</div>
	);
};
